import {
  SeededRandom,
  type RandomSeed,
} from '../random/SeededRandom.js'
import { MAX_PARTICLE_COUNT } from './particleBudget.js'

export const DEFAULT_MAX_PARTICLE_CAPACITY = MAX_PARTICLE_COUNT
export const NO_PARTICLE_OWNER = 0xffff_ffff

/** Largest finite IEEE-754 binary32 magnitude accepted by particle storage. */
export const MAX_FINITE_FLOAT32 = 3.4028234663852886e38

const MAX_REAL_PARTICLE_OWNER = NO_PARTICLE_OWNER - 1

const MIN_GROWTH_CAPACITY = 8

export interface ParticleTargets {
  readonly count: number
  readonly x: Float32Array
  readonly y: Float32Array
  readonly size?: Float32Array
  readonly rotation?: Float32Array
  readonly alpha?: Float32Array
  readonly wordIndex?: Uint32Array
  readonly letterIndex?: Uint32Array
}

export interface ParticleInitializationOptions {
  readonly seed: RandomSeed
  readonly chaosRadius: number
  /**
   * Canvas dimensions. When both are provided, the chaos endpoint is scattered
   * to a uniform random point WITHIN the canvas (matching the v1 engine) so
   * particles never animate off-screen. When omitted, the initializer falls
   * back to the legacy order-relative scatter bounded by `chaosRadius`.
   */
  readonly canvasWidth?: number
  readonly canvasHeight?: number
}

interface ParticleArrays {
  positionX: Float32Array
  positionY: Float32Array
  previousPositionX: Float32Array
  previousPositionY: Float32Array
  orderX: Float32Array
  orderY: Float32Array
  chaosX: Float32Array
  chaosY: Float32Array
  size: Float32Array
  rotation: Float32Array
  alpha: Float32Array
  wordIndex: Uint32Array
  letterIndex: Uint32Array
}

function assertNonNegativeInteger(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${label} must be a non-negative integer`)
  }
}

function createArrays(capacity: number): ParticleArrays {
  return {
    positionX: new Float32Array(capacity),
    positionY: new Float32Array(capacity),
    previousPositionX: new Float32Array(capacity),
    previousPositionY: new Float32Array(capacity),
    orderX: new Float32Array(capacity),
    orderY: new Float32Array(capacity),
    chaosX: new Float32Array(capacity),
    chaosY: new Float32Array(capacity),
    size: new Float32Array(capacity),
    rotation: new Float32Array(capacity),
    alpha: new Float32Array(capacity),
    wordIndex: new Uint32Array(capacity),
    letterIndex: new Uint32Array(capacity),
  }
}

function copyActiveParticles(
  source: ParticleBuffer,
  destination: ParticleArrays,
): void {
  const activeCount = source.count
  destination.positionX.set(source.positionX.subarray(0, activeCount))
  destination.positionY.set(source.positionY.subarray(0, activeCount))
  destination.previousPositionX.set(source.previousPositionX.subarray(0, activeCount))
  destination.previousPositionY.set(source.previousPositionY.subarray(0, activeCount))
  destination.orderX.set(source.orderX.subarray(0, activeCount))
  destination.orderY.set(source.orderY.subarray(0, activeCount))
  destination.chaosX.set(source.chaosX.subarray(0, activeCount))
  destination.chaosY.set(source.chaosY.subarray(0, activeCount))
  destination.size.set(source.size.subarray(0, activeCount))
  destination.rotation.set(source.rotation.subarray(0, activeCount))
  destination.alpha.set(source.alpha.subarray(0, activeCount))
  destination.wordIndex.set(source.wordIndex.subarray(0, activeCount))
  destination.letterIndex.set(source.letterIndex.subarray(0, activeCount))
}

function clearRange(particles: ParticleBuffer, start: number, end: number): void {
  particles.positionX.fill(0, start, end)
  particles.positionY.fill(0, start, end)
  particles.previousPositionX.fill(0, start, end)
  particles.previousPositionY.fill(0, start, end)
  particles.orderX.fill(0, start, end)
  particles.orderY.fill(0, start, end)
  particles.chaosX.fill(0, start, end)
  particles.chaosY.fill(0, start, end)
  particles.size.fill(0, start, end)
  particles.rotation.fill(0, start, end)
  particles.alpha.fill(0, start, end)
  particles.wordIndex.fill(0, start, end)
  particles.letterIndex.fill(0, start, end)
}

export class ParticleBuffer {
  private _count = 0
  private _capacity: number
  private _maxCapacity: number

  private _positionX: Float32Array
  private _positionY: Float32Array
  private _previousPositionX: Float32Array
  private _previousPositionY: Float32Array
  private _orderX: Float32Array
  private _orderY: Float32Array
  private _chaosX: Float32Array
  private _chaosY: Float32Array
  private _size: Float32Array
  private _rotation: Float32Array
  private _alpha: Float32Array
  private _wordIndex: Uint32Array
  private _letterIndex: Uint32Array

  constructor(
    initialCapacity = 0,
    maxCapacity = DEFAULT_MAX_PARTICLE_CAPACITY,
  ) {
    assertNonNegativeInteger(initialCapacity, 'Initial capacity')
    assertNonNegativeInteger(maxCapacity, 'Maximum capacity')
    if (maxCapacity > MAX_PARTICLE_COUNT) {
      throw new RangeError(
        `Maximum capacity cannot exceed particle hard cap ${MAX_PARTICLE_COUNT}`,
      )
    }
    if (initialCapacity > maxCapacity) {
      throw new RangeError('Initial capacity cannot exceed maximum capacity')
    }

    const arrays = createArrays(initialCapacity)
    this._capacity = initialCapacity
    this._maxCapacity = maxCapacity
    this._positionX = arrays.positionX
    this._positionY = arrays.positionY
    this._previousPositionX = arrays.previousPositionX
    this._previousPositionY = arrays.previousPositionY
    this._orderX = arrays.orderX
    this._orderY = arrays.orderY
    this._chaosX = arrays.chaosX
    this._chaosY = arrays.chaosY
    this._size = arrays.size
    this._rotation = arrays.rotation
    this._alpha = arrays.alpha
    this._wordIndex = arrays.wordIndex
    this._letterIndex = arrays.letterIndex
  }

  get count(): number {
    return this._count
  }

  get capacity(): number {
    return this._capacity
  }

  get maxCapacity(): number {
    return this._maxCapacity
  }

  get positionX(): Float32Array {
    return this._positionX
  }

  get positionY(): Float32Array {
    return this._positionY
  }

  get previousPositionX(): Float32Array {
    return this._previousPositionX
  }

  get previousPositionY(): Float32Array {
    return this._previousPositionY
  }

  get orderX(): Float32Array {
    return this._orderX
  }

  get orderY(): Float32Array {
    return this._orderY
  }

  get chaosX(): Float32Array {
    return this._chaosX
  }

  get chaosY(): Float32Array {
    return this._chaosY
  }

  get size(): Float32Array {
    return this._size
  }

  get rotation(): Float32Array {
    return this._rotation
  }

  get alpha(): Float32Array {
    return this._alpha
  }

  get wordIndex(): Uint32Array {
    return this._wordIndex
  }

  get letterIndex(): Uint32Array {
    return this._letterIndex
  }

  setCount(nextCount: number): void {
    assertNonNegativeInteger(nextCount, 'Particle count')
    if (nextCount > this._maxCapacity) {
      throw new RangeError(
        `Particle count ${nextCount} exceeds maximum capacity ${this._maxCapacity}`,
      )
    }

    const previousCount = this._count
    this.ensureCapacity(nextCount)

    if (nextCount < previousCount) {
      clearRange(this, nextCount, previousCount)
    }
    this._count = nextCount
  }

  private ensureCapacity(requiredCapacity: number): void {
    if (requiredCapacity <= this._capacity) {
      return
    }

    let nextCapacity = Math.min(
      Math.max(this._capacity, MIN_GROWTH_CAPACITY),
      this._maxCapacity,
    )
    while (nextCapacity < requiredCapacity) {
      nextCapacity = Math.min(nextCapacity * 2, this._maxCapacity)
    }

    // Build the complete replacement first. A failed allocation cannot expose a
    // half-replaced struct-of-arrays buffer to the live renderer.
    const replacement = createArrays(nextCapacity)
    copyActiveParticles(this, replacement)

    this._positionX = replacement.positionX
    this._positionY = replacement.positionY
    this._previousPositionX = replacement.previousPositionX
    this._previousPositionY = replacement.previousPositionY
    this._orderX = replacement.orderX
    this._orderY = replacement.orderY
    this._chaosX = replacement.chaosX
    this._chaosY = replacement.chaosY
    this._size = replacement.size
    this._rotation = replacement.rotation
    this._alpha = replacement.alpha
    this._wordIndex = replacement.wordIndex
    this._letterIndex = replacement.letterIndex
    this._capacity = nextCapacity
  }
}

interface ValidatedParticleTargets {
  readonly count: number
  readonly x: Float32Array
  readonly y: Float32Array
  readonly size: Float32Array | undefined
  readonly rotation: Float32Array | undefined
  readonly alpha: Float32Array | undefined
  readonly wordIndex: Uint32Array | undefined
  readonly letterIndex: Uint32Array | undefined
  readonly seed: RandomSeed
  readonly chaosRadius: number
  readonly canvasWidth: number | undefined
  readonly canvasHeight: number | undefined
}

interface ParticleInitializationSnapshot {
  readonly count: number
  readonly x: unknown
  readonly y: unknown
  readonly size: unknown
  readonly rotation: unknown
  readonly alpha: unknown
  readonly wordIndex: unknown
  readonly letterIndex: unknown
  readonly seed: RandomSeed
  readonly chaosRadius: number
  readonly canvasWidth: number | undefined
  readonly canvasHeight: number | undefined
}

function snapshotInitializationInputs(
  targets: ParticleTargets,
  options: ParticleInitializationOptions,
): ParticleInitializationSnapshot {
  // Caller-owned containers may expose getters. Capture each property once,
  // before validation and before any live buffer state can change.
  const count = targets.count
  const x = targets.x
  const y = targets.y
  const size = targets.size
  const rotation = targets.rotation
  const alpha = targets.alpha
  const wordIndex = targets.wordIndex
  const letterIndex = targets.letterIndex
  const seed = options.seed
  const chaosRadius = options.chaosRadius
  const canvasWidth = options.canvasWidth
  const canvasHeight = options.canvasHeight

  return {
    count,
    x,
    y,
    size,
    rotation,
    alpha,
    wordIndex,
    letterIndex,
    seed,
    chaosRadius,
    canvasWidth,
    canvasHeight,
  }
}

function validateFloat32Target(
  values: unknown,
  count: number,
  label: string,
  required: true,
): Float32Array
function validateFloat32Target(
  values: unknown,
  count: number,
  label: string,
  required: false,
): Float32Array | undefined
function validateFloat32Target(
  values: unknown,
  count: number,
  label: string,
  required: boolean,
): Float32Array | undefined {
  if (values === undefined) {
    if (required) {
      throw new RangeError(`${label} target array is required`)
    }
    return undefined
  }

  if (
    !ArrayBuffer.isView(values)
    || Object.getPrototypeOf(values) !== Float32Array.prototype
  ) {
    throw new TypeError(`${label} target array must be an exact Float32Array`)
  }

  const target = values as Float32Array
  if (!(target.buffer instanceof ArrayBuffer)) {
    throw new TypeError(`${label} target array must use a transferable ArrayBuffer`)
  }
  if (target.length !== count) {
    throw new RangeError(
      `${label} target array length ${target.length} must equal particle count ${count}`,
    )
  }

  return target
}

function validateUint32Target(
  values: unknown,
  count: number,
  label: string,
): Uint32Array | undefined {
  if (values === undefined) {
    return undefined
  }

  if (
    !ArrayBuffer.isView(values)
    || Object.getPrototypeOf(values) !== Uint32Array.prototype
  ) {
    throw new TypeError(`${label} target array must be an exact Uint32Array`)
  }

  const target = values as Uint32Array
  if (!(target.buffer instanceof ArrayBuffer)) {
    throw new TypeError(`${label} target array must use a transferable ArrayBuffer`)
  }
  if (target.length !== count) {
    throw new RangeError(
      `${label} target array length ${target.length} must equal particle count ${count}`,
    )
  }

  return target
}

function validateFiniteScalars(values: Float32Array, label: string): void {
  for (let index = 0; index < values.length; index += 1) {
    if (!Number.isFinite(values[index])) {
      throw new RangeError(`${label} target at index ${index} must be finite`)
    }
  }
}

function validateChaosCoordinateRange(
  values: Float32Array,
  label: string,
  chaosRadius: number,
): void {
  const maximumCoordinateMagnitude = MAX_FINITE_FLOAT32 - chaosRadius
  for (let index = 0; index < values.length; index += 1) {
    if (Math.abs(values[index] ?? 0) > maximumCoordinateMagnitude) {
      throw new RangeError(
        `${label} target at index ${index} plus chaos radius exceeds finite Float32 range`,
      )
    }
  }
}

function validateSizes(values: Float32Array | undefined): void {
  if (values === undefined) {
    return
  }

  validateFiniteScalars(values, 'size')
  for (let index = 0; index < values.length; index += 1) {
    if ((values[index] ?? 0) < 0) {
      throw new RangeError(`size target at index ${index} must be non-negative`)
    }
  }
}

function validateAlpha(values: Float32Array | undefined): void {
  if (values === undefined) {
    return
  }

  validateFiniteScalars(values, 'alpha')
  for (let index = 0; index < values.length; index += 1) {
    const value = values[index] ?? 0
    if (value < 0 || value > 1) {
      throw new RangeError(`alpha target at index ${index} must be between 0 and 1`)
    }
  }
}

function validateOwners(values: Uint32Array | undefined, label: string): void {
  if (values === undefined) {
    return
  }

  for (let index = 0; index < values.length; index += 1) {
    const value = values[index] ?? NO_PARTICLE_OWNER
    if (value !== NO_PARTICLE_OWNER && value > MAX_REAL_PARTICLE_OWNER) {
      throw new RangeError(
        `${label} target at index ${index} must be an owner index or the no-owner sentinel`,
      )
    }
  }
}

function validateTargets(
  snapshot: ParticleInitializationSnapshot,
  maxCapacity: number,
): ValidatedParticleTargets {
  const count = snapshot.count
  assertNonNegativeInteger(count, 'Target count')
  if (count > maxCapacity) {
    throw new RangeError(
      `Target count ${count} exceeds maximum capacity ${maxCapacity}`,
    )
  }
  const chaosRadius = snapshot.chaosRadius
  if (!Number.isFinite(chaosRadius) || chaosRadius < 0) {
    throw new RangeError('Chaos radius must be a non-negative finite number')
  }
  if (chaosRadius > MAX_FINITE_FLOAT32) {
    throw new RangeError('Chaos radius exceeds finite Float32 range')
  }
  const canvasWidth = snapshot.canvasWidth
  const canvasHeight = snapshot.canvasHeight
  if (canvasWidth !== undefined && (!Number.isFinite(canvasWidth) || canvasWidth < 0 || canvasWidth > MAX_FINITE_FLOAT32)) {
    throw new RangeError('Canvas width must be a non-negative finite number within Float32 range')
  }
  if (canvasHeight !== undefined && (!Number.isFinite(canvasHeight) || canvasHeight < 0 || canvasHeight > MAX_FINITE_FLOAT32)) {
    throw new RangeError('Canvas height must be a non-negative finite number within Float32 range')
  }

  // Validate only the captured values before live storage can grow. Concrete
  // ArrayBuffer-backed views cannot execute indexed getters during the copy.
  const x = validateFloat32Target(snapshot.x, count, 'x', true)
  const y = validateFloat32Target(snapshot.y, count, 'y', true)
  const size = validateFloat32Target(snapshot.size, count, 'size', false)
  const rotation = validateFloat32Target(snapshot.rotation, count, 'rotation', false)
  const alpha = validateFloat32Target(snapshot.alpha, count, 'alpha', false)
  const wordIndex = validateUint32Target(snapshot.wordIndex, count, 'wordIndex')
  const letterIndex = validateUint32Target(snapshot.letterIndex, count, 'letterIndex')

  validateFiniteScalars(x, 'x')
  validateFiniteScalars(y, 'y')
  validateChaosCoordinateRange(x, 'x', chaosRadius)
  validateChaosCoordinateRange(y, 'y', chaosRadius)
  validateSizes(size)
  if (rotation !== undefined) {
    validateFiniteScalars(rotation, 'rotation')
  }
  validateAlpha(alpha)
  validateOwners(wordIndex, 'wordIndex')
  validateOwners(letterIndex, 'letterIndex')

  return {
    count,
    x,
    y,
    size,
    rotation,
    alpha,
    wordIndex,
    letterIndex,
    seed: snapshot.seed,
    chaosRadius,
    canvasWidth,
    canvasHeight,
  }
}

/**
 * Copies layout targets into the live buffer and derives stable chaos origins.
 * The loop writes scalar values directly into reusable arrays and does not create
 * per-particle objects.
 */
export function initializeParticleTargets(
  particles: ParticleBuffer,
  targets: ParticleTargets,
  options: ParticleInitializationOptions,
): void {
  const snapshot = snapshotInitializationInputs(targets, options)
  const validated = validateTargets(snapshot, particles.maxCapacity)
  const random = new SeededRandom(validated.seed)
  particles.setCount(validated.count)

  const radius = validated.chaosRadius
  const canvasWidth = validated.canvasWidth
  const canvasHeight = validated.canvasHeight
  // When canvas dimensions are supplied, scatter the chaos endpoint to a uniform
  // random point WITHIN the canvas — matching the v1 engine so particles animate
  // entirely on-screen instead of an order-relative offset of up to a full canvas
  // diagonal (which flew off). Both branches consume exactly two RNG draws per
  // particle, keeping the seeded sequence length identical.
  const size = validated.size
  const rotation = validated.rotation
  const alpha = validated.alpha
  const wordIndex = validated.wordIndex
  const letterIndex = validated.letterIndex

  for (let index = 0; index < validated.count; index += 1) {
    const orderX = validated.x[index] ?? 0
    const orderY = validated.y[index] ?? 0
    const chaosX = canvasWidth !== undefined
      ? random.nextFloat() * canvasWidth
      : orderX + (random.nextFloat() * 2 - 1) * radius
    const chaosY = canvasHeight !== undefined
      ? random.nextFloat() * canvasHeight
      : orderY + (random.nextFloat() * 2 - 1) * radius

    particles.orderX[index] = orderX
    particles.orderY[index] = orderY
    particles.chaosX[index] = chaosX
    particles.chaosY[index] = chaosY
    particles.positionX[index] = chaosX
    particles.positionY[index] = chaosY
    particles.previousPositionX[index] = chaosX
    particles.previousPositionY[index] = chaosY
    particles.size[index] = size?.[index] ?? 1
    particles.rotation[index] = rotation?.[index] ?? 0
    particles.alpha[index] = alpha?.[index] ?? 1
    particles.wordIndex[index] = wordIndex?.[index] ?? NO_PARTICLE_OWNER
    particles.letterIndex[index] = letterIndex?.[index] ?? NO_PARTICLE_OWNER
  }
}
